import React, { useEffect, useState } from "react";

import { Box, Button, CircularProgress } from "@material-ui/core";
import SvgIcon from "@material-ui/core/SvgIcon";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import HeaderContainer from "@remar/shared/dist/components/HeaderContainer/HeaderContainer";
import { StyledCheckbox, TransparentButton } from "@remar/shared/dist/ui/Buttons/styles";
import convertMinsToHrsMins from "@remar/shared/dist/utils/convertMinsToHrsMins";
import { validatePositiveNumber } from "@remar/shared/dist/utils/serviceUtils/validators";
import { uploadFile } from "@remar/shared/dist/utils/stateUtils";
import { cloneDeep } from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
	createChapter,
	editChapter,
	fetchCourse,
	getFullState,
	setStateValue
} from "store/features/Course/course.slice";
import { _emit, emit } from "store/features/notifications/notifications.slice";

import { CourseChapterCreateDto, CourseChapterUpdateDto, genericService } from "store/services";

import { routes } from "core/constants";

import Card from "modules/Components/Card";
import EditCard from "modules/Components/EditCard";
import EmptyViewCard from "modules/Components/EmptyViewCard";

import EditFields from "./EditFields";

import { AddNewCategory, ButtonContainer, CardContainer, Container } from "./styles";

import { ListItems } from "../Chapters/styles";
import { ChapterViewInterface } from "../types";
import { reorderChapters } from "../utils";

const Content = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const params = useParams<{ courseId: string }>();
	const { activeCourse, editChapterData, addChapterData, isLoading, uploadingImage } = useSelector(getFullState);
	const chapters = activeCourse ? activeCourse.chapters! : [];
	const courseId = Number(params.courseId);
	const defaultAddChapterFields: CourseChapterCreateDto = {
		order: 0,
		courseId,
		name: "",
		totalTimeLengthInMinutes: 0,
		calculateTimeLengthAutomatically: false,
		progressTrackingEnabled: false,
		mainImageKey: ""
	};
	const [editIndex, setEditIndex] = useState<number | null>(null);
	const [showAddNewChapter, setShowAddNewChapter] = useState<boolean>(false);
	const [chapterLength, setChapterLength] = useState({ hours: "", mins: "" });
	const [addChapterLength, setAddChapterLength] = useState({ hours: "", mins: "" });
	const [disableAddChapter, setDisableAddChapter] = useState(false);

	const [chapterView, setChapterView] = useState<ChapterViewInterface[]>([]);

	useEffect(() => {
		dispatch(fetchCourse(courseId));
		if (!editChapterData) {
			dispatch(
				setStateValue({ key: "editChapterData", value: { data: { ...defaultAddChapterFields }, filters: { id: 0 } } })
			);
		}
	}, [dispatch]);

	useEffect(() => {
		if (chapters.length !== 0) {
			populateData(chapters);
		}
	}, [chapters]);

	const handleSave = () => {
		const {
			name,
			totalTimeLengthInMinutes,
			calculateTimeLengthAutomatically,
			progressTrackingEnabled,
			hours,
			mins,
			excludedFromCourseRequirement
		} = editChapterData!.data;
		const payload = {
			data: {
				name,
				totalTimeLengthInMinutes,
				calculateTimeLengthAutomatically,
				progressTrackingEnabled,
				excludedFromCourseRequirement,
				...(editChapterData!.data.mainImageKey && { mainImageKey: editChapterData!.data.mainImageKey })
			},
			filters: editChapterData!.filters
		};
		if (!calculateTimeLengthAutomatically) {
			payload.data.totalTimeLengthInMinutes = Number(hours) * 60 + Number(mins);
		}
		dispatch(editChapter(payload));
		setEditIndex(null);
		dispatch(setStateValue({ key: "editChapterData", value: null }));
		resetEditData();
		setDisableAddChapter(false);
	};

	const handleEdit = (i: number) => {
		const {
			id,
			name,
			totalTimeLengthInMinutes,
			calculateTimeLengthAutomatically,
			progressTrackingEnabled,
			mainImageUrl,
			mainImageKey,
			excludedFromCourseRequirement
		} = chapters![i]!;
		const minHrs = convertMinsToHrsMins(totalTimeLengthInMinutes).split("h");
		const hours = Number(minHrs[0]);
		const mins = Number(minHrs[1].split("min")[0]);
		const payload = {
			data: {
				name,
				mainImageUrl,
				mainImageKey,
				totalTimeLengthInMinutes,
				hours,
				mins,
				calculateTimeLengthAutomatically,
				progressTrackingEnabled,
				excludedFromCourseRequirement
			},
			filters: { id }
		} as unknown as CourseChapterUpdateDto;
		setEditIndex(i);
		dispatch(setStateValue({ key: "editChapterData", value: payload }));
	};

	const handleCancel = () => {
		setEditIndex(null);
		dispatch(setStateValue({ key: "editChapterData", value: null }));
		populateData(chapters);
		resetEditData();
		setDisableAddChapter(false);
	};

	const updateEditChapterLength = (val: string, key) => {
		if (updateChapterLength(val, key)) {
			updateEditData(val, key);
		}
	};

	const updateEditData = (val: string | boolean | number, key) => {
		const _editData = cloneDeep(editChapterData || ({} as CourseChapterUpdateDto));
		_editData.data[key] = val;
		dispatch(setStateValue({ key: "editChapterData", value: _editData }));
	};

	const populateData = chapters => {
		if (chapters.length) {
			const data: ChapterViewInterface[] = (chapters || []).map((el, i) => {
				const {
					name,
					totalSections,
					totalTimeLengthInMinutes,
					id,
					totalLessons,
					order,
					isActive,
					excludedFromCourseRequirement
				} = el;
				return {
					id: id,
					order: order,
					title: {
						key: "Chapter Title",
						subtitle: isActive ? "Published" : "Unpublished",
						value: name
					},
					content: [
						{ key: "Sections", value: totalSections },
						{ key: "Total Lessons", value: totalLessons },
						{ key: "Chapter Length", value: convertMinsToHrsMins(totalTimeLengthInMinutes) }
					],
					imgSrc: el.mainImageUrl,
					bottomComponent: (
						<ButtonContainer>
							<TransparentButton
								variant="text"
								onClick={() => {
									handleEdit(i);
									setDisableAddChapter(true);
								}}
							>
								Edit Chapter
							</TransparentButton>

							<Button
								variant={"contained"}
								color={"primary"}
								onClick={() => history.push(`${routes.course.getPath()}/${courseId}/chapters/${id}`)}
							>
								View Chapter
							</Button>
						</ButtonContainer>
					),
					bottomLeftComponent: (
						<Box mt={2}>
							<StyledCheckbox
								checked={excludedFromCourseRequirement}
								onChange={(event: React.ChangeEvent<{ checked: unknown }>) => {
									updateEditData(!!event.target.checked, "excludedFromCourseRequirement");
									const { id } = chapters![i]!;
									const payload = {
										data: {
											excludedFromCourseRequirement: !!event.target.checked
										},
										filters: { id }
									};
									dispatch(editChapter(payload, false));
								}}
							/>
							<span>Exclude from course requirement </span>
						</Box>
					)
				};
			});
			setChapterView(data);
		} else {
			setChapterView([]);
		}
	};

	const addNewChapter = () => {
		dispatch(
			setStateValue({ key: "addChapterData", value: { data: { ...defaultAddChapterFields }, filters: { id: 0 } } })
		);
		setShowAddNewChapter(true);
	};

	const breadcrumb = [
		{ title: "Course", key: 0, link: routes.course.getPath() },
		{ title: !!activeCourse ? activeCourse.name : "", key: 1 }
	];

	const createNewChapter = () => {
		const {
			order,
			name: chapterName,
			totalTimeLengthInMinutes,
			calculateTimeLengthAutomatically,
			progressTrackingEnabled,
			excludedFromCourseRequirement
		} = addChapterData!.data;

		if (chapters.find(chapter => chapter.name.toLowerCase() === addChapterName!.toLowerCase())) {
			dispatch(
				emit({
					message: "Chapter Name already exists",
					color: "error"
				})
			);
			return;
		}

		const payload = {
			order,
			courseId,
			name: chapterName!.trim(),
			totalTimeLengthInMinutes,
			calculateTimeLengthAutomatically,
			progressTrackingEnabled,
			excludedFromCourseRequirement,
			...(addChapterData!.data.mainImageKey && { mainImageKey: addChapterData!.data.mainImageKey })
		};
		if (payload.calculateTimeLengthAutomatically) {
			payload.totalTimeLengthInMinutes = 0;
		} else {
			payload.totalTimeLengthInMinutes = Number(addChapterLength.hours) * 60 + Number(addChapterLength.mins);
		}
		dispatch(createChapter(payload as CourseChapterCreateDto));
		resetAddChapterData();
	};

	const resetEditData = () => {
		setShowAddNewChapter(false);
		dispatch(
			setStateValue({ key: "editChapterData", value: { data: { ...defaultAddChapterFields }, filters: { id: 0 } } })
		);
		setChapterLength({ hours: "", mins: "" });
	};

	const resetAddChapterData = () => {
		setShowAddNewChapter(false);
		dispatch(
			setStateValue({ key: "addChapterData", value: { data: { ...defaultAddChapterFields }, filters: { id: 0 } } })
		);
		setAddChapterLength({ hours: "", mins: "" });
	};

	const updateChapterLength = (val: string, key) => {
		if (validatePositiveNumber(val)) {
			const _chapterLength = { ...chapterLength };
			_chapterLength[key] = val;
			setChapterLength(_chapterLength);
			return true;
		}
		return false;
	};

	const updateAddChapterLength = (val: string, key) => {
		if (validatePositiveNumber(val)) {
			const _chapterLength = { ...addChapterLength };
			_chapterLength[key] = val;
			setAddChapterLength(_chapterLength);
			return true;
		}
		return false;
	};

	const updateAddChapterData = (val: unknown, key) => {
		dispatch(setStateValue({ key: `addChapterData.data.${key}`, value: val }));
	};

	const {
		name: addChapterName,
		calculateTimeLengthAutomatically: addChapterCalculateTimeLengthAutomatically,
		progressTrackingEnabled: addChapterProgressTrackingEnabled
	} = addChapterData ? addChapterData.data : ({} as CourseChapterCreateDto);

	const disabledAddChapterSaveBtn = !addChapterCalculateTimeLengthAutomatically
		? !(addChapterLength.hours && addChapterLength.mins && addChapterName)
		: !addChapterName;

	const onDragEnd = async result => {
		if (!result.destination) {
			return;
		}
		const { reorderedChapters, selectedChapterId } = reorderChapters(chapters, result.destination, result.source);
		if (selectedChapterId) {
			//Mock order change to reflect on UI without lag
			dispatch(setStateValue({ key: "activeCourse.chapters", value: reorderedChapters }));
			await dispatch(
				editChapter(
					{
						data: {
							order: result.destination.index
						},
						filters: {
							id: selectedChapterId
						}
					},
					false,
					false
				)
			);
		}
	};

	const chapterCard = (el, i) => {
		if (i === editIndex) {
			const {
				name,
				calculateTimeLengthAutomatically,
				hours,
				mins,
				progressTrackingEnabled,
				excludedFromCourseRequirement
			} = editChapterData!.data;
			const editChapterLength = { hours, mins };
			const disabledSaveBtn =
				(!calculateTimeLengthAutomatically
					? !(editChapterLength.hours !== "" && editChapterLength.mins !== "" && name)
					: !name) || uploadingImage;
			return (
				<Box mb={4}>
					<EditCard
						rightComponent={EditFields(
							updateEditData,
							name,
							calculateTimeLengthAutomatically,
							progressTrackingEnabled,
							updateEditChapterLength,
							chapterLength,
							disabledSaveBtn,
							handleCancel,
							handleSave,
							editChapterLength,
							excludedFromCourseRequirement
						)}
						imageUrl={activeCourse!.chapters![i].mainImageUrl!}
						width={850}
						handleImageUpdate={(file, handleProgres) => {
							dispatch(
								uploadFile(
									{ file },
									{
										_emit,
										genericApiService: genericService,
										imageUrlStatePath: `activeCourse.chapters.${i}.mainImageUrl`,
										loaderStatePath: "uploadingImage",
										s3KeyStatePath: "editChapterData.data.mainImageKey",
										setStateValue,
										onProgress: ({ loaded, total }) => {
											handleProgres && handleProgres({ progress: (loaded / total) * 100, uploading: true });
										},
										uploadCb: () => {
											handleProgres && handleProgres({ progress: 0, uploading: false });
										}
									}
								)
							);
						}}
					/>
				</Box>
			);
		} else {
			return (
				<CardContainer key={el.id}>
					<Card
						width={850}
						title={el.title}
						content={el.content}
						imgSrc={el.imgSrc}
						imgRounded={true}
						rightComponent={el.bottomComponent}
						bottomLeftComponent={el.bottomLeftComponent}
					/>
				</CardContainer>
			);
		}
	};

	return (
		<Container>
			<HeaderContainer
				heading={!!activeCourse ? activeCourse.name : ""}
				breadcrumb={breadcrumb}
				actions={
					<AddNewCategory disabled={disableAddChapter} variant={"contained"} color={"primary"} onClick={addNewChapter}>
						Add New Chapter
					</AddNewCategory>
				}
			/>
			{showAddNewChapter && (
				<Box px={3} py={1}>
					<EditCard
						rightComponent={EditFields(
							updateAddChapterData,
							addChapterName,
							addChapterCalculateTimeLengthAutomatically,
							addChapterProgressTrackingEnabled,
							updateAddChapterLength,
							addChapterLength,
							disabledAddChapterSaveBtn,
							resetAddChapterData,
							createNewChapter
						)}
						width={850}
						handleImageUpdate={(file, handleProgres) => {
							dispatch(
								uploadFile(
									{ file },
									{
										_emit,
										onProgress: ({ loaded, total }) => {
											handleProgres && handleProgres({ progress: (loaded / total) * 100, uploading: true });
										},
										uploadCb: () => {
											handleProgres && handleProgres({ progress: 0, uploading: false });
										},
										genericApiService: genericService,
										imageUrlStatePath: "addChapterData.data.mainImageUrl",
										loaderStatePath: "uploadingImage",
										s3KeyStatePath: "addChapterData.data.mainImageKey",
										setStateValue
									}
								)
							);
						}}
					/>
				</Box>
			)}
			{isLoading ? (
				<Box display="flex" alignItems="center" justifyContent="center" height={450}>
					<CircularProgress size="7rem" color="primary" thickness={5} variant="indeterminate" />
				</Box>
			) : chapterView.length > 0 ? (
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId={`${courseId}`}>
						{provided => (
							<Box px={3} py={1} {...provided.droppableProps} ref={provided.innerRef}>
								{chapterView.map((item, index) => (
									<Draggable key={item.id} draggableId={`${item.id}`} index={index}>
										{provided => (
											<ListItems ref={provided.innerRef} {...provided.draggableProps}>
												<Box flexDirection="column">
													{index !== editIndex && (
														<Box
															display="flex"
															justifyContent="center"
															style={{ backgroundColor: "#2a2e37" }}
															{...provided.dragHandleProps}
															mr={3}
														>
															<SvgIcon fontSize="large" color="disabled">
																<DragHandleIcon />
															</SvgIcon>
														</Box>
													)}
													{chapterCard(item, index)}
												</Box>
											</ListItems>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</Box>
						)}
					</Droppable>
				</DragDropContext>
			) : (
				!showAddNewChapter && (
					<CardContainer>
						<EmptyViewCard title="There are no Chapters yet" />
					</CardContainer>
				)
			)}
		</Container>
	);
};

export default Content;
